import * as THREE from 'three';

/**
 * Raw-Three.js port of the workspace's CustomGrid (src/components/workspace/scene/CustomGrid.tsx),
 * used for the 3D view of the 2D polygon flow (CadCanvas, which is plain Three.js rather than R3F).
 *
 * Reproduces drei's <Grid> shader: an infinite, camera-following grid that fades with distance,
 * plus world-fixed green origin axes.
 */

/** Slightly below z=0 so the grid sits under the polygons (z ≈ 0) without z-fighting. */
const GRID_Z_OFFSET = -0.1;

/** Multiplier applied to the grid shader's line alpha (matches CustomGrid). */
const CELL_LINE_ALPHA_BOOST = 2;

const vertexShader = /* glsl */ `
  varying vec3 localPosition;
  varying vec4 worldPosition;

  uniform vec3 worldCamProjPosition;
  uniform vec3 worldPlanePosition;
  uniform float fadeDistance;
  uniform bool infiniteGrid;
  uniform bool followCamera;

  void main() {
    localPosition = position.xzy;
    if (infiniteGrid) localPosition *= 1.0 + fadeDistance;

    worldPosition = modelMatrix * vec4(localPosition, 1.0);
    if (followCamera) {
      worldPosition.xyz += (worldCamProjPosition - worldPlanePosition);
      localPosition = (inverse(modelMatrix) * worldPosition).xyz;
    }

    gl_Position = projectionMatrix * viewMatrix * worldPosition;
  }
`;

const fragmentShader = /* glsl */ `
  varying vec3 localPosition;
  varying vec4 worldPosition;

  uniform vec3 worldCamProjPosition;
  uniform float cellSize;
  uniform float sectionSize;
  uniform vec3 cellColor;
  uniform vec3 sectionColor;
  uniform float fadeDistance;
  uniform float fadeStrength;
  uniform float fadeFrom;
  uniform float cellThickness;
  uniform float sectionThickness;

  float getGrid(float size, float thickness) {
    vec2 r = localPosition.xz / size;
    vec2 grid = abs(fract(r - 0.5) - 0.5) / fwidth(r);
    float line = min(grid.x, grid.y) + 1.0 - thickness;
    return 1.0 - min(line, 1.0);
  }

  void main() {
    float g1 = getGrid(cellSize, cellThickness);
    float g2 = getGrid(sectionSize, sectionThickness);

    vec3 from = worldCamProjPosition * vec3(fadeFrom);
    float dist = distance(from, worldPosition.xyz);
    float d = 1.0 - min(dist / fadeDistance, 1.0);
    vec3 color = mix(cellColor, sectionColor, min(1.0, sectionThickness * g2));

    gl_FragColor = vec4(color, (g1 + g2) * pow(d, fadeStrength));
    gl_FragColor.a = mix(0.75 * gl_FragColor.a, gl_FragColor.a, g2);
    gl_FragColor.a = min(gl_FragColor.a * ${CELL_LINE_ALPHA_BOOST.toFixed(1)}, 1.0);
    if (gl_FragColor.a <= 0.0) discard;

    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }
`;

export interface CadGrid {
  group: THREE.Group;
  /**
   * Call once per frame to keep the grid centered under the camera and sized to the view.
   * Pass the orbit `target` for a perspective camera so the grid stays stable while orbiting.
   */
  update: (camera: THREE.Camera, target?: THREE.Vector3) => void;
  dispose: () => void;
}

export function createCadGrid(): CadGrid {
  const material = new THREE.ShaderMaterial({
    uniforms: {
      cellSize: { value: 20 },
      // Section pass disabled — CustomGrid uses a single uniform cell grid + explicit axes.
      sectionSize: { value: 20 },
      // fadeDistance is recomputed every frame from the camera's view size (see update).
      fadeDistance: { value: 5000 },
      fadeStrength: { value: 1 },
      fadeFrom: { value: 1 },
      cellThickness: { value: 1 },
      sectionThickness: { value: 0 },
      cellColor: { value: new THREE.Color('#333333') },
      sectionColor: { value: new THREE.Color('#333333') },
      infiniteGrid: { value: true },
      followCamera: { value: true },
      worldCamProjPosition: { value: new THREE.Vector3() },
      worldPlanePosition: { value: new THREE.Vector3() },
    },
    vertexShader,
    fragmentShader,
    transparent: true,
    side: THREE.DoubleSide,
    depthWrite: false,
  });
  material.polygonOffset = true;
  material.polygonOffsetFactor = -4;
  material.polygonOffsetUnits = -4;

  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), material);
  mesh.frustumCulled = false;
  // drei's shader treats the plane as a Y-up floor (position.xzy); rotate it onto the world XY plane.
  mesh.rotation.x = Math.PI / 2;
  mesh.position.z = GRID_Z_OFFSET;
  mesh.renderOrder = -1;

  const group = new THREE.Group();
  group.add(mesh);

  // Origin axes (green), separate from the fading cell grid — matches CustomGrid.
  // Endpoints are rewritten every frame (see update) so the axes always span the whole
  // viewport and never show a visible end, regardless of zoom or pan.
  const axisMaterial = new THREE.LineBasicMaterial({
    color: '#4FD12E',
    transparent: true,
    opacity: 0.85,
    depthWrite: false,
  });
  const xAxis = new THREE.Line(
    new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(), new THREE.Vector3()]),
    axisMaterial,
  );
  xAxis.frustumCulled = false;
  xAxis.renderOrder = -1;
  const yAxis = new THREE.Line(
    new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(), new THREE.Vector3()]),
    axisMaterial,
  );
  yAxis.frustumCulled = false;
  yAxis.renderOrder = -1;
  group.add(xAxis, yAxis);

  const plane = new THREE.Plane();
  const upVector = new THREE.Vector3(0, 1, 0);
  const zeroVector = new THREE.Vector3(0, 0, 0);

  /** How far beyond the visible radius the grid/axes extend — keeps every edge off-screen. */
  const VIEW_MARGIN = 10;

  const update = (camera: THREE.Camera, target?: THREE.Vector3) => {
    mesh.updateMatrixWorld();
    plane.setFromNormalAndCoplanarPoint(upVector, zeroVector).applyMatrix4(mesh.matrixWorld);
    const camProj = material.uniforms.worldCamProjPosition.value as THREE.Vector3;
    plane.projectPoint(camera.position, camProj);
    material.uniforms.worldPlanePosition.value.set(0, 0, 0).applyMatrix4(mesh.matrixWorld);

    // Radius of the visible area on the grid plane, in world units.
    let viewRadius: number;
    if ((camera as THREE.OrthographicCamera).isOrthographicCamera) {
      const ortho = camera as THREE.OrthographicCamera;
      viewRadius = Math.max(ortho.right - ortho.left, ortho.top - ortho.bottom) / (2 * ortho.zoom);
    } else {
      const persp = camera as THREE.PerspectiveCamera;
      // Use the orbit distance (camera → target), not the perpendicular height to the plane:
      // at grazing angles the height collapses to ~0 and would shrink the grid out of view.
      const dist = target ? camera.position.distanceTo(target) : camera.position.distanceTo(camProj);
      viewRadius = dist * Math.tan((persp.fov * Math.PI) / 360);
    }

    // Grow the fade region (and therefore the plane) so its edge stays well outside the view.
    const reach = Math.max(viewRadius * VIEW_MARGIN, 1000);
    material.uniforms.fadeDistance.value = reach;

    // Keep the axes centered under the camera so they always cross the full viewport.
    const xPos = xAxis.geometry.attributes.position as THREE.BufferAttribute;
    xPos.setXYZ(0, camProj.x - reach, 0, GRID_Z_OFFSET);
    xPos.setXYZ(1, camProj.x + reach, 0, GRID_Z_OFFSET);
    xPos.needsUpdate = true;

    const yPos = yAxis.geometry.attributes.position as THREE.BufferAttribute;
    yPos.setXYZ(0, 0, camProj.y - reach, GRID_Z_OFFSET);
    yPos.setXYZ(1, 0, camProj.y + reach, GRID_Z_OFFSET);
    yPos.needsUpdate = true;
  };

  const dispose = () => {
    mesh.geometry.dispose();
    material.dispose();
    xAxis.geometry.dispose();
    yAxis.geometry.dispose();
    axisMaterial.dispose();
  };

  return { group, update, dispose };
}
